<script setup>
/* global axios */
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  cardId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['close', 'started']);
const { t } = useI18n();
const store = useStore();

const definitions = ref([]);
const selectedDefinitionId = ref('');
const isLoading = ref(false);
const isStarting = ref(false);
const error = ref('');
const definitionSelectRef = ref(null);
const metadataSeparator = '·';

const definitionOptions = computed(() =>
  definitions.value.map(definition => ({
    value: definition.id,
    label: `${definition.name} (${definition.step_count} ${t(
      'PIPELINE.SEQUENCES.STEPS_UNIT'
    )})`,
  }))
);

const selectedDefinition = computed(() =>
  definitions.value.find(
    definition => definition.id === selectedDefinitionId.value
  )
);

const focusDefinitionSelect = async () => {
  await nextTick();
  definitionSelectRef.value?.$el?.querySelector('select')?.focus();
};

const closeModal = () => {
  if (!isStarting.value) emit('close');
};

const fetchDefinitions = async () => {
  if (isLoading.value) return;

  error.value = '';
  isLoading.value = true;
  try {
    const accountId = store.getters.getCurrentAccountId;
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId}/pipeline/activity_sequences?active=true&per_page=50`
    );
    definitions.value = data.data || [];
  } catch {
    definitions.value = [];
    error.value = t('PIPELINE.SEQUENCES.FETCH_DEFINITIONS_ERROR');
  } finally {
    isLoading.value = false;
    if (definitions.value.length) await focusDefinitionSelect();
  }
};

const handleStart = async () => {
  if (!selectedDefinitionId.value || isStarting.value) return;

  isStarting.value = true;
  error.value = '';
  try {
    await PipelineCardsAPI.startSequence(
      props.cardId,
      selectedDefinitionId.value
    );
    emit('started');
  } catch (requestError) {
    error.value =
      requestError.response?.data?.error || t('PIPELINE.SEQUENCES.START_ERROR');
  } finally {
    isStarting.value = false;
  }
};

onMounted(fetchDefinitions);
</script>

<template>
  <Modal
    show
    :close-on-backdrop-click="!isStarting"
    :close-on-escape="!isStarting"
    :show-close-button="!isStarting"
    initial-focus-selector="#sequence-definition"
    @close="closeModal"
  >
    <section
      class="flex max-h-[min(80vh,44rem)] flex-col overflow-hidden"
      :aria-busy="isLoading || isStarting"
      data-modal="start-sequence"
    >
      <header class="border-b border-n-weak px-4 py-4 sm:px-6">
        <h2 class="pr-10 text-base font-semibold text-n-slate-12">
          {{ t('PIPELINE.SEQUENCES.MODAL_TITLE') }}
        </h2>
      </header>

      <div class="overflow-y-auto px-4 py-5 sm:px-6">
        <div
          v-if="isLoading"
          class="flex min-h-32 flex-col items-center justify-center gap-3 text-n-slate-11"
          role="status"
          aria-live="polite"
        >
          <Spinner class="!size-6" />
          <span class="text-sm">{{ t('PIPELINE.SEQUENCES.LOADING') }}</span>
        </div>

        <div
          v-else-if="error && definitions.length === 0"
          class="flex min-h-32 flex-col items-center justify-center gap-4 text-center"
          role="alert"
        >
          <p class="text-sm text-n-ruby-11">{{ error }}</p>
          <Button
            variant="faded"
            color="slate"
            size="lg"
            icon="i-lucide-rotate-ccw"
            :label="t('PIPELINE.SEQUENCES.RETRY')"
            @click="fetchDefinitions"
          />
        </div>

        <p
          v-else-if="definitions.length === 0"
          class="min-h-32 content-center text-center text-sm text-n-slate-11"
        >
          {{ t('PIPELINE.SEQUENCES.NO_DEFINITIONS') }}
        </p>

        <div v-else class="space-y-4">
          <div class="space-y-2">
            <label
              for="sequence-definition"
              class="text-sm font-medium text-n-slate-12"
            >
              {{ t('PIPELINE.SEQUENCES.SELECT_DEFINITION') }}
            </label>
            <Select
              ref="definitionSelectRef"
              v-model="selectedDefinitionId"
              input-id="sequence-definition"
              name="sequence-definition"
              class="w-full [&>select]:!h-12 [&>select]:!w-full"
              :disabled="isStarting"
              :options="definitionOptions"
              :placeholder="t('PIPELINE.SEQUENCES.SELECT_PLACEHOLDER')"
            />
          </div>

          <div
            v-if="selectedDefinition"
            class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
          >
            <p class="mb-1 text-xs font-medium text-n-slate-12">
              {{ selectedDefinition.name }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ selectedDefinition.step_count }}
              {{ t('PIPELINE.SEQUENCES.STEPS_UNIT') }}
              <template v-if="selectedDefinition.total_duration_days">
                {{ metadataSeparator }}
                {{ selectedDefinition.total_duration_days }}
                {{ t('PIPELINE.SEQUENCES.DAYS_TOTAL') }}
              </template>
            </p>
          </div>

          <p v-if="error" class="text-sm text-n-ruby-11" role="alert">
            {{ error }}
          </p>
        </div>
      </div>

      <footer
        class="flex flex-col-reverse gap-2 border-t border-n-weak px-4 py-4 sm:flex-row sm:justify-end sm:px-6"
      >
        <Button
          variant="ghost"
          color="slate"
          size="lg"
          :label="t('PIPELINE.SEQUENCES.CANCEL')"
          :disabled="isStarting"
          class="w-full sm:w-auto"
          @click="closeModal"
        />
        <Button
          v-if="definitions.length"
          size="lg"
          :label="
            isStarting
              ? t('PIPELINE.SEQUENCES.STARTING')
              : t('PIPELINE.SEQUENCES.START_BUTTON')
          "
          :is-loading="isStarting"
          :disabled="!selectedDefinitionId || isStarting"
          class="w-full sm:w-auto"
          @click="handleStart"
        />
      </footer>
    </section>
  </Modal>
</template>
